import { useState } from 'react'
import { Organization, User } from '../api'
import { DataTable, ColumnDef } from '@/components/ui/data-table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Users, UserPlus, Search } from 'lucide-react'
import { useOrganizationUsers } from '../hooks/useOrganizationUsers'
import { AddOrganizationUserDialog } from './AddOrganizationUserDialog'

interface OrganizationUsersTabProps {
  organization: Organization
  disabled?: boolean
}

export function OrganizationUsersTab({ organization, disabled = false }: OrganizationUsersTabProps) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [addUserDialog, setAddUserDialog] = useState(false)
  const limit = 10

  const { data, isLoading } = useOrganizationUsers(organization._id, page, limit, search)

  const isDefaultOrg = organization.name === 'default'

  const handleMigrateUser = () => {
    setAddUserDialog(true)
  }

  // Define table columns
  const columns: ColumnDef<User>[] = [
    {
      key: 'user',
      header: 'User',
      cell: (user) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
            <Users className="h-4 w-4" />
          </div>
          <div>
            <div className="font-medium">
              {user.name || user.username || 'No name'}
            </div>
            <div className="text-sm text-muted-foreground">{user.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      cell: (user) => (
        <Badge variant={user.role === 'ADMIN' ? 'default' : 'secondary'}>
          {user.role}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Joined',
      cell: (user) => (
        <div className="text-sm text-muted-foreground">
          {new Date(user.createdAt).toLocaleDateString()}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      {/* Header with Migrate User button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64"
              disabled={disabled}
            />
          </div>
        </div>
        
        {!isDefaultOrg && !disabled && (
          <Button onClick={handleMigrateUser}>
            <UserPlus className="mr-2 h-4 w-4" />
            Migrate Users
          </Button>
        )}
      </div>

      {/* Users Table */}
      <DataTable
        columns={columns}
        data={data?.users || []}
        isLoading={isLoading}
        emptyMessage="No users in this organization yet"
        emptyIcon={<Users className="h-12 w-12 text-muted-foreground" />}
        pagination={
          data
            ? {
                currentPage: data.page,
                totalItems: data.total,
                pageSize: limit,
                onPageChange: setPage,
              }
            : undefined
        }
      />

      {/* Migrate User Dialog */}
      {addUserDialog && (
        <AddOrganizationUserDialog
          organization={organization}
          onClose={() => setAddUserDialog(false)}
        />
      )}
    </div>
  )
}
